import { Container, Text } from '@pixi/react'
import { DropDetail, WinLineDetail } from '../../../domain/betDetail/models/BetDetailResponse'
import { SpinType } from '../../../domain/betDetail/store/betDetailStore'
import { formatToCommaWithLang, Image, ScrollBox, t } from '@slot-nx/core'
import { TextStyle } from 'pixi.js'
import { getSymbolImg } from '../../../domain/game/util/gameUtil'

const whiteTextStyle = new TextStyle({
  fontSize: 32,
  fontWeight: 'bold',
  fill: '#FFFFFF',
})

const TriggerLine = ({ data, index }: { data: { type: number, count: number }, index: number }) => (
  <Container name="triggerLine" x={56} y={80 * index}>
    <Image image="winLineDetail_listBg" x={-43} y={0} width={950} height={79} />
    <Container x={50} y={40}>
      {Array.from({ length: data.count }).map((_, i) => <Image key={i} anchor={0.5} width={100} height={80} x={i * 50} y={0} image={getSymbolImg({ type: data.type, multiplier: 0 })} />)}
    </Container>
    <Text name="lineCount" text="--" position={[400, 40]} anchor={[0, 0.5]} style={whiteTextStyle} />
    <Text name="linePayout" text={t('buyBonus_popup_free_game')} x={850} y={40} anchor={[1, 0.5]} style={whiteTextStyle} />
  </Container>
)

const WinningLine = ({ data, index }: { data: WinLineDetail, index: number }) => (
  <Container name="winDetail" x={56} y={80 * index}>
    <Image image="winLineDetail_listBg" x={-43} y={0} width={950} height={79} />
    <Image anchor={0.5} width={100} height={80} position={[50, 40]} image={getSymbolImg({ type: data.type, multiplier: 0 })} />
    <Text name="lineCount" text={`x ${data.count}`} position={[150, 40]} anchor={[0, 0.5]} style={whiteTextStyle} />
    <Text name="linePayout" text={formatToCommaWithLang(data.payout)} x={850} y={40} anchor={[1, 0.5]} style={whiteTextStyle} />
  </Container>
)

const AccMultiplierInfo = ({ accMultiplier }: { accMultiplier: number }) => (
  <Container name="accMultiplierInfo" x={110} y={1075}>
    <Image image="winLineDetail_listBg" x={-43} y={0} width={950} height={79} />
    <Text text={t('betDetail_win_accmultiplier_title')} x={0} y={40} anchor={[0, 0.5]} style={whiteTextStyle} />
    <Text text={`${accMultiplier} x`} x={500} y={40} anchor={[0, 0.5]} style={whiteTextStyle} />
  </Container>
)

const MultiplierInfo = ({ totalMultiplier }: { totalMultiplier: number }) => (
  <Container name="multiplierInfo" x={110} y={1075 + 80}>
    <Image image="winLineDetail_listBg" x={-43} y={0} width={950} height={79} />
    <Text text={t('betDetail_win_multiplier_title')} x={0} y={40} anchor={[0, 0.5]} style={whiteTextStyle} />
    <Text text={`${totalMultiplier} x`} x={500} y={40} anchor={[0, 0.5]} style={whiteTextStyle} />
    <Text text={t('betDetail_win_multiplier_applied')} x={750} y={40} anchor={[0, 0.5]} style={new TextStyle({ fontSize: 32, fontWeight: 'bold', fill: '#aaaaaa' })} />
  </Container>
)

const BasePayoutInfo = ({ basePayout }: { basePayout: number }) => (
  <Container name="baseOddsInfo" x={110} y={1075 + 160}>
    <Image image="winLineDetail_listBg" x={-43} y={0} width={950} height={79} />
    <Text text={t('betDetail_win_basepayout_title')} x={0} y={40} anchor={[0, 0.5]} style={whiteTextStyle} />
    <Text text={`${basePayout}`} x={750} y={40} anchor={[0, 0.5]} style={new TextStyle({ fontSize: 32, fontWeight: 'bold', fill: '#aaaaaa' })} />
  </Container>
)

const FooterSummary = ({ detail: currentGameDetail }: { detail: DropDetail }) => {
  const payoutText = currentGameDetail?.basePayout ? `${currentGameDetail?.totalMultiplier} x ${currentGameDetail?.basePayout}` : '0'
  return (
    <Container visible={currentGameDetail.isLastDrop}>
      <Text name="payoutTitle" text={t('betList_payout')} x={100} y={1450} anchor={[0, 0.5]} style={new TextStyle({ fontSize: 42, fontWeight: 'bold', fill: '#FFFFFF' })} />
      <Text name="totalPayout" text={payoutText} x={950} y={1450} anchor={[1, 0.5]} style={new TextStyle({ fontSize: 36, fill: '#0ef7f7' })} />
      <Text name="totalPayoutTitle" text={t('betDetail_winLine_total')} x={100} y={1530} anchor={[0, 0.5]} style={new TextStyle({ fontSize: 42, fontWeight: 'bold', fill: '#FFFFFF' })} />
      <Text name="totalPayout" text={formatToCommaWithLang(currentGameDetail.multiplierPayout)} x={950} y={1530} anchor={[1, 0.5]} style={new TextStyle({ fontSize: 45, fontWeight: 'bold', fill: '#F7E584' })} />
    </Container>
  )
}

interface WinningDetailProps {
  currentGameDetail: DropDetail | null
  selectSpinType: SpinType
  triggerGameDetail: { type: number, count: number }[]
}

export const WinningDetail = (props: WinningDetailProps) => {
  const { currentGameDetail, triggerGameDetail } = props

  if (!currentGameDetail) return null

  const hasTrigger = triggerGameDetail.length > 0
  const hasWinLines = currentGameDetail.winLineList.length > 0
  const triggerOffset = hasTrigger ? triggerGameDetail.length : 0

  return (
    <Container name="winDetail">
      <Image image="winLineDetail_bg" x={50} y={1050} width={980} height={540} />
      {hasTrigger || hasWinLines
        ? (
            <ScrollBox x={50} y={1075} width={970} height={368}>
              {hasTrigger && triggerGameDetail.map((item, idx) => (
                <TriggerLine key={`trig_${idx}`} data={item} index={idx} />
              ))}
              {hasWinLines && currentGameDetail.winLineList.map((item, idx) => (
                <WinningLine key={`win_${idx}`} data={item} index={triggerOffset + idx} />
              ))}
            </ScrollBox>
          )
        : (
            currentGameDetail.isLastDrop && currentGameDetail.basePayout > 0 && (
              <>
                <AccMultiplierInfo accMultiplier={currentGameDetail.accMultiplier} />
                <MultiplierInfo totalMultiplier={currentGameDetail.totalMultiplier} />
                <BasePayoutInfo basePayout={currentGameDetail.basePayout} />
              </>
            )
          )}
      <FooterSummary detail={currentGameDetail} />
    </Container>
  )
}
